import 'bootstrap/dist/css/bootstrap.min.css';
import '../../Styles/MainTable.css';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '../../AuthContext';
import SearchBox from '../../Components/MainTable/SearchBox';
import ViewToggle from '../../Components/MainTable/ViewToggle';
import PlusIcon from '../../Components/Icons/PlusIcon';

import { getUsers } from '../../services/users';
import { getProjectsV2, getProjectOwnersV2, searchProjectsV2 } from '../../services/projectsV2';
import type { User } from '../../types/user';
import { ACCOUNT_TYPE } from '../../constants/authConstants';
import TourGuide from '../../Components/MainTable/TourGuide';
import { Dropdown, type MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import ProjectList from './ProjectList';
import type { ProjectV2 } from 'src/types/projectV2';
import Loader from 'src/Components/Common/Loader';

type View = 'all' | 'user';
const internalDomains = [
  'modularclosets.com',
  'alcoveclosets.com',
  'hexacoder.com',
  'wtioutsourcing.com',
];

const ProjectTable: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isTester = ['amenexmk@gmail.com', 'blagoja.koneski@mkmage.com'].includes(user.email);
  const isExternalUser =
    user.role === 'user-external' ||
    !internalDomains.some((domain) => user.email.toLowerCase().endsWith(`@${domain}`));

  const allowedViews: View[] = isTester || isExternalUser ? ['user'] : ['user', 'all'];

  const [view, setView] = useState<View>(allowedViews[0]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'All' | 'Quoted' | 'Draft'>('All');
  const [users, setUsers] = useState<User[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('All');
  const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const navigate = useNavigate();

  const newProjectMenuItems: MenuProps['items'] = [
    {
      key: 'blueprints',
      label: 'I have Blueprints',
      onClick: () => navigate('/add-project?type=blueprint'),
    },
    {
      key: 'design',
      label: 'Design my Space',
      onClick: () => navigate('/add-project?type=design'),
    },
  ];

  const showOwnerSelect =
    (view === 'all' && user.accountType === ACCOUNT_TYPE.MULTIFAMILY) || user.isDev;

  useEffect(() => {
    getUsers().then(setUsers);
  }, []);
  const isSearchActive = debouncedSearch.trim().length > 0 || selectedOwner !== 'All';

  const { data, isLoading, isError, error, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['projects-v2', view, user],
      queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
        getProjectsV2(view == 'all', pageParam as string | undefined),
      getNextPageParam: (lastPage) => lastPage?.cursor || undefined,
      initialPageParam: undefined,
      enabled: !isSearchActive,
    });

  const projectData = useMemo(() => data?.pages.flatMap((page) => page.projects) ?? [], [data]);
  const lastCursor = data?.pages?.[data.pages.length - 1]?.cursor;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const {
    data: searchData,
    isLoading: isSearching,
    isError: isSearchError,
    error: searchError,
  } = useQuery({
    queryKey: ['projects-v2-search', view, debouncedSearch, selectedOwner],
    queryFn: () => {
      const email =
        view === allowedViews[0] ? user.email : selectedOwner === 'All' ? undefined : selectedOwner;
      const res = searchProjectsV2(debouncedSearch, email);
      return res;
    },
    enabled: isSearchActive,
  });

  const searchProjects = useMemo(
    () =>
      searchData?.hits?.map((hit) => hit?._source).filter((p): p is ProjectV2 => Boolean(p)) ?? [],
    [searchData],
  );

  const activeProjects = isSearchActive ? searchProjects : projectData;

  const { data: ownersData } = useQuery({
    queryKey: ['projects-v2-owners'],
    queryFn: getProjectOwnersV2,
  });

  useEffect(() => {
    if (ownersData) setOwners([...ownersData].sort());
  }, [ownersData]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    let observer: IntersectionObserver | null = null;

    if (node && hasNextPage && !isSearchActive) {
      observer = new IntersectionObserver(
        (entries) => {
          const first = entries[0];
          if (first?.isIntersecting && hasNextPage && !isFetchingNextPage && !isSearchActive) {
            fetchNextPage();
          }
        },
        { root: null, rootMargin: '200px', threshold: 0 },
      );

      observer.observe(node);
    }

    return () => {
      observer?.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isSearchActive]);
  const handleChangeTab = (v: View) => {
    setSearchText('');
    setDebouncedSearch('');
    setSelectedOwner('All');
    queryClient.removeQueries({ queryKey: ['projects-v2'] });
    queryClient.removeQueries({ queryKey: ['projects-v2-search'] });
    setView(v);
  };

  if (isLoading) {
    return (
      <div className='text-center py-5'>
        <Loader />
        <p className='mt-2 text-muted'>Loading...</p>
      </div>
    );
  }

  if (isError || isSearchError) {
    return (
      <div className='text-center py-5'>
        <span>
          Error:{' '}
          {(isSearchError ? (searchError as Error)?.message : (error as Error)?.message) ||
            'Fetch failed'}
        </span>
      </div>
    );
  }

  return (
    <>
      <div className='container py-4 main-table-page'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <h1 className='heading'>Projects</h1>
          <div className='d-flex search-button-wrapper'>
            <SearchBox value={searchText} onChange={setSearchText} />
            <Dropdown
              menu={{ items: newProjectMenuItems }}
              trigger={['click']}
              className='add-new-project'
            >
              <a className='simple-button'>
                <PlusIcon />
                New Project
              </a>
            </Dropdown>
          </div>
        </div>

        <div className='filter-wrapper d-flex justify-content-between mb-4 '>
          <div className='d-flex align-items-center gap-2'>
            <ViewToggle
              view={view}
              onChange={(v) => {
                handleChangeTab(v);
              }}
              allowed={allowedViews}
            />
            {view == 'all' && showOwnerSelect && (
              <div className='owner-select-wrapper '>
                <select
                  className='form-select owner-select'
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                >
                  <option value='All'>All Owners</option>
                  {owners.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {isSearching ? (
          <div className='text-center py-5'>
            <Loader />
            <p className='mt-2 text-muted'>Loading...</p>
          </div>
        ) : (
          activeProjects && (
            <ProjectList
              view={view}
              statusFilter={statusFilter}
              users={users}
              projects={activeProjects}
              selectedOwner='All'
              cursor={isSearchActive ? null : lastCursor}
            />
          )
        )}
        {!isSearchActive && <div ref={sentinelRef} style={{ height: 1 }} />}
        {!isSearchActive && isFetchingNextPage && (
          <div className='text-center py-5'>
            <Loader />
            <p className='mt-2 text-muted'>Loading more...</p>
          </div>
        )}
      </div>
      <TourGuide isTourOpen={isTourOpen} setIsTourOpen={setIsTourOpen} />
    </>
  );
};

export default ProjectTable;
